import React, {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import PropTypes from 'prop-types'
import { Icon, classNames, useUpdateRef } from '@zambezi/sdk/common'
import useFilteredDropdownList from './useFilteredDropdownList'
import Lookup from './Lookup'
import { inputGhostRenderer, suggestionTextRenderer } from './renderers'

/**
 * Create a React Filtered Dropdown component
 *
 * @template T
 * @template Item
 * @param {import('./interfaces').FilteredDropdownProps<T, Item>} props
 * @param {React.Ref<import('./interfaces').LookupRef>} ref
 * @returns {JSX.Element}
 */
function filteredDropdown (
  {
    className,
    defaultSelectedItemsIndices,
    disabledItemsIndices,
    filter,
    getGroupChildren: propsGetGroupChildren,
    getSuggestionValue,
    iconProps,
    multiple,
    onChange,
    onSelectedItemsChanged: propsOnSelectedItemsChanged,
    openOnFocus,
    renderInput,
    renderGroupLabel,
    renderSuggestion,
    selectable,
    selectedItemsIndices,
    shouldRenderNoResultsMessage,
    suggestionClassName,
    suggestionProps,
    suggestions,
    suggestionsFetchRequested,
    withGrouping,
    enableVirtualScroll,
    ...props
  },
  ref
) {
  const isMultiple = selectable && multiple
  const [value, setValue] = useState('')
  /** @type {React.RefObject<import('./interfaces').LookupRef>} */
  const elementRef = useRef()
  /** @type {React.RefObject<HTMLElement>} */
  const inputRef = useRef()

  /**
   * Called when the selected items have changed.
   * @type {(indices: number[], data: Item, event: React.SyntheticEvent<HTMLElement, Event>) => void}
   */
  const handleSelectedItemsChanged = useCallback(
    (indices, _, event) => {
      propsOnSelectedItemsChanged(event, indices)
    },
    [propsOnSelectedItemsChanged]
  )

  /** @type {(suggestion: {value: Item}) => string} */
  const suggestionValueGetter = useCallback(
    suggestion => getSuggestionValue(suggestion.value),
    [getSuggestionValue]
  )

  const {
    data,
    disabledItemsIndices: disabledIndices,
    onSelectedItemsChanged,
    getGroupChildren,
    renderGroupLabel: renderGroup,
    renderItem,
    selectedItemsIndices: selectedIndices
  } = useFilteredDropdownList(
    {
      data: suggestions,
      defaultSelectedItemsIndices,
      disabledItemsIndices,
      filter: value,
      getGroupChildren: propsGetGroupChildren,
      getItemValue: getSuggestionValue,
      renderItem: renderSuggestion,
      renderGroupLabel,
      onSelectedItemsChanged: handleSelectedItemsChanged,
      selectedItemsIndices,
      withGrouping
    },
    isMultiple
  )

  /**
   * Called when a suggestion is selected in Lookup dropdown.
   * Calls the callback coming from useFilteredDropdownList to populate the selected indices.
   * @type {(event: React.SyntheticEvent<HTMLElement, Event>, data: {method: 'click' | 'enter', suggestion: {index: number, value: any}, suggestionIndex: number}) => void}
   */
  const handleSuggestionSelected = useCallback(
    (event, { suggestion, suggestionIndex }) => {
      const indices = getMappedSelectedIndices(
        selectedIndices,
        suggestionIndex,
        selectable,
        multiple
      )
      onSelectedItemsChanged(indices, suggestion, event)
      if (selectable && multiple && elementRef.current) {
        elementRef.current.open()
      }
    },
    [selectable, multiple, selectedIndices, onSelectedItemsChanged]
  )
  /**
   * Called when a change happens in the Lookup
   * @type {(event: React.SyntheticEvent<HTMLElement, Event>, props: {method: 'type'|'escape'|'enter'|'click', value: string}) => void}
   */
  const handleChange = useCallback((event, props) => {
    if (props.method === 'type') setValue(props.value)
    onChange(event, props)
  }, [])
  const _renderGroupLabel = useMemo(() => {
    if (isMultiple) {
      /**
       * @type {(d: T, i: number) => JSX.Element}
       */
      return (d, i) => (
        <div
          className={classNames({
            'zb-multiselect-dropdown-list-group-is-selected': isGroupSelected(
              data[i].group,
              selectedIndices,
              disabledIndices,
              getGroupOffset(data, i)
            )
          })}
        >
          <span className='zb-multiselect-dropdown-list-group-label-icon'>
            <Icon name='core-dropdown-item-selected' title='Selected Group' />
          </span>

          {renderGroup(d, i)}
        </div>
      )
    }
    return renderGroup
  }, [isMultiple, renderGroup, data, selectedIndices, disabledIndices])
  /** @type {(suggestion: Item, query: import('./interfaces').SuggestionQuery<Item>, index: number) => JSX.Element} */
  const _renderSuggestion = useMemo(() => {
    if (selectable) {
      if (multiple) {
        return (suggestion, props, index) => {
          const isSelected = selectedIndices.indexOf(index) !== -1
          return (
            <div>
              <span className='zb-multiselect-dropdown-list-item-icon'>
                <Icon
                  name='core-dropdown-item-selected'
                  title='Selected Item'
                  iconProps={iconProps}
                />
              </span>

              {renderItem(
                suggestion,
                { ...props, getSuggestionValue },
                index,
                isSelected
              )}
            </div>
          )
        }
      }
      return (suggestion, props, index) => {
        const isSelected = selectedIndices.indexOf(index) !== -1
        return (
          <div>
            {renderItem(
              suggestion,
              { ...props, getSuggestionValue },
              index,
              isSelected
            )}

            {isSelected && (
              <Icon
                name='core-dropdown-item-selected'
                title='Selected Item'
                aria-hidden='true'
                iconProps={iconProps}
              />
            )}
          </div>
        )
      }
    }
    return (suggestion, props, index) => {
      return renderItem(suggestion, { ...props, getSuggestionValue }, index)
    }
  }, [
    selectable,
    multiple,
    renderItem,
    getSuggestionValue,
    selectedIndices,
    iconProps
  ])
  /** @type {(suggestion: Item, query: import('./interfaces').SuggestionQuery<Item>, index: number) => string} */
  const _suggestionClassName = useCallback(
    (suggestion, props, index) => {
      const isSelected = selectable && selectedIndices.indexOf(index) !== -1
      return classNames(
        {
          'zb-multiselect-dropdown-list-item-is-selected':
            multiple && isSelected,
          'zb-select-dropdown-list-item-is-selected': !multiple && isSelected
        },
        suggestionClassName(suggestion, props, index)
      )
    },
    [selectable, multiple, selectedIndices, suggestionClassName]
  )
  /** @type {(suggestion: Item, query: import('./interfaces').SuggestionQuery<Item>, index: number) => React.HTMLAttributes<HTMLElement>} */
  const _suggestionProps = useCallback(
    (suggestion, props, index) => {
      if (selectable) {
        return {
          'aria-selected': selectedIndices.indexOf(index) !== -1,
          ...suggestionProps(suggestion, props, index)
        }
      }
      return false
    },
    [selectable, selectedIndices, suggestionProps]
  )
  const shouldRenderSuggestions = useCallback(
    (value, event) => {
      const hasSuggestions = !!suggestions.length
      if (event.type === 'focus') {
        return openOnFocus && hasSuggestions
      }
      return hasSuggestions
    },
    [suggestions, openOnFocus]
  )

  /** @type {import('./interfaces').InputRenderer<T, Item>} */
  const renderFilteredInput = useCallback(
    props => {
      const { ref, isFocused, isDisabled } = props
      const iconName = isFocused
        ? 'core-combobox-expanded'
        : 'core-combobox-collapsed'

      const inputProps = {
        type: 'search',
        ...props,
        ref: el => {
          if (typeof ref === 'function') {
            ref(el)
          } else if (ref && 'current' in ref) {
            ref.current = el
          }
          inputRef.current = el
        }
      }

      return (
        <span>
          {renderInput(inputProps)}

          <Icon
            name={iconName}
            onClick={isDisabled ? null : handleIconClick}
            aria-hidden='true'
            title=''
            iconProps={iconProps}
          />
        </span>
      )

      function handleIconClick () {
        const input = inputRef.current
        // isOpen ? element.close() : element.open()
        input && ((input.element || input)).focus()
      }
    },
    [renderInput]
  )
  /** @type {import('./interfaces').GroupLabelClickHandler<T>} */
  const handleGroupLabelClick = useCallback(
    (group, index, event) => {
      if (isMultiple) {
        const indices = getGroupMappedSelectedIndices(
          data,
          selectedIndices,
          disabledIndices,
          index
        )
        onSelectedItemsChanged(indices, event, group)
      }
    },
    [data, selectedIndices, disabledIndices, onSelectedItemsChanged, isMultiple]
  )
  /**
   * @type {(props: {reason: 'escape-pressed'|'enter-pressed'|'input-focused', value: string}) => void}
   */
  const handleSuggestionsFetchRequested = useCallback(
    props => {
      if (props.reason === 'escape-pressed') setValue('')
      suggestionsFetchRequested(props)
    },
    [suggestionsFetchRequested]
  )
  useEffect(() => {
    setValue(filter || '')
  }, [filter])
  useUpdateRef(ref, elementRef)
  return (
    <Lookup
      {...props}
      suggestionsFetchRequested={handleSuggestionsFetchRequested}
      className={classNames('zb-filtered-dropdown', 'zb-combobox', className)}
      disabledItemsIndices={disabledIndices}
      dropdownClass={isMultiple ? 'zb-multiselect-dropdown-list' : undefined}
      getGroupChildren={getGroupChildren}
      getSuggestionValue={suggestionValueGetter}
      listProps={{
        'aria-multiselectable': isMultiple
      }}
      onChange={handleChange}
      onSuggestionSelected={handleSuggestionSelected}
      onGroupLabelClick={handleGroupLabelClick}
      ref={elementRef}
      renderGroupLabel={_renderGroupLabel}
      renderInput={renderFilteredInput}
      renderSuggestion={_renderSuggestion}
      shouldRenderNoResultsMessage={shouldRenderNoResultsMessage}
      shouldRenderSuggestions={shouldRenderSuggestions}
      suggestionClassName={_suggestionClassName}
      suggestionProps={_suggestionProps}
      suggestions={data}
      value={value}
      withGrouping={withGrouping}
      enableVirtualScroll={enableVirtualScroll}
    />
  )
}

function noop () {}

/**
 * @template T
 * @template Item
 * @type {React.ForwardRefExoticComponent<import('./interfaces').FilteredDropdownProps<T, Item> & React.RefAttributes<import('./interfaces').LookupRef>>}
 */
const FilteredDropdown = forwardRef(filteredDropdown)

FilteredDropdown.defaultProps = {
  disabledItemsIndices: [],
  suggestions: [],
  filter: '',
  getSuggestionValue: String,
  getGroupChildren: () => [],
  onChange: noop,
  openOnFocus: true,
  onSelectedItemsChanged: noop,
  renderInput: inputGhostRenderer,
  renderGroupLabel: String,
  renderSuggestion: suggestionTextRenderer,
  shouldRenderNoResultsMessage: (_, suggestions) => suggestions.length === 0,
  suggestionProps: noop,
  suggestionClassName: noop,
  suggestionsFetchRequested: noop,
  withGrouping: false,
  enableVirtualScroll: false
}
FilteredDropdown.propTypes = {
  defaultSelectedItemsIndices: PropTypes.arrayOf(PropTypes.number),
  disabledItemsIndices: PropTypes.arrayOf(PropTypes.number),
  filter: PropTypes.string,
  getGroupChildren: PropTypes.func,
  getSuggestionValue: PropTypes.func,
  inputProps: PropTypes.object,
  isError: PropTypes.bool,
  isDisabled: PropTypes.bool,
  multiple: PropTypes.bool,
  onBlur: PropTypes.func,
  onChange: PropTypes.func,
  onFocus: PropTypes.func,
  openOnFocus: PropTypes.bool,
  onSelectedItemsChanged: PropTypes.func,
  renderInput: PropTypes.func,
  renderGroupLabel: PropTypes.func,
  renderSuggestion: PropTypes.func,
  selectable: PropTypes.bool,
  selectedItemsIndices: PropTypes.arrayOf(PropTypes.number),
  shouldRenderNoResultsMessage: PropTypes.func,
  suggestionClassName: PropTypes.func,
  suggestionProps: PropTypes.func,
  suggestions: PropTypes.array,
  suggestionsFetchRequested: PropTypes.func,
  withGrouping: PropTypes.bool,
  enableVirtualScroll: PropTypes.bool
}
export default FilteredDropdown

/**
 * Get the list of selected indices.
 *
 * @param {number[]} selectedIndices
 * @param {number} suggestionIndex
 * @param {boolean} selectable
 * @param {boolean} multiple
 * @returns {number[]}
 */
function getMappedSelectedIndices (
  selectedIndices,
  suggestionIndex,
  selectable,
  multiple
) {
  if (selectable && multiple) {
    if (selectedIndices.indexOf(suggestionIndex) === -1) {
      return selectedIndices.concat(suggestionIndex)
    } else {
      return selectedIndices.filter(index => index !== suggestionIndex)
    }
  } else {
    return [suggestionIndex]
  }
}

/**
 * Get the list of selected indices for a group.
 *
 * @param {Array<{group: Array<any>}>} data
 * @param {number[]} selectedIndices
 * @param {number[]} disabledIndices
 * @param {number} index
 * @returns {number[]}
 */
function getGroupMappedSelectedIndices (
  data,
  selectedIndices,
  disabledIndices,
  index
) {
  const indexOffset = getGroupOffset(data, index)
  const { group } = data[index]
  if (isGroupSelected(group, selectedIndices, disabledIndices, indexOffset)) {
    return selectedIndices.filter(
      index =>
        index < indexOffset ||
        index >= indexOffset + group.length ||
        disabledIndices.indexOf(indexOffset + index) !== -1
    )
  }
  const addedIndices = []
  for (let j = 0; j < group.length; j++) {
    const itemIndex = j + indexOffset
    if (
      selectedIndices.indexOf(itemIndex) === -1 &&
      disabledIndices.indexOf(itemIndex) === -1
    ) {
      addedIndices.push(itemIndex)
    }
  }
  return selectedIndices.concat(addedIndices)
}

/**
 * Get the list of selected indices for a group.
 *
 * @param {Array<any>} group
 * @param {number[]} selectedIndices
 * @param {number[]} disabledIndices
 * @param {number} indexOffset
 * @returns {number[]}
 */
function isGroupSelected (
  group,
  selectedIndices,
  disabledIndices,
  indexOffset
) {
  return group.every(
    (_, index) =>
      selectedIndices.indexOf(index + indexOffset) !== -1 ||
      disabledIndices.indexOf(index + indexOffset) !== -1
  )
}

/**
 * Get the list of selected indices for a group.
 *
 * @param {Array<{group: Array<any>}>} data
 * @param {number} index
 * @returns {number}
 */
function getGroupOffset (data, index) {
  let indexOffset = 0
  for (let i = 0; i < data.length; i++) {
    if (i === index) return indexOffset
    else indexOffset += data[i].group.length
  }
  return 0
}
